import * as z from "zod";

export const keyboardModelSchema = z
  .object({
    v: z.string(),
    i: z.string().min(1),
    n: z.record(z.string().min(1), z.string()),
  })
  .readonly();

export type KeyboardModel = z.infer<typeof keyboardModelSchema>;

export interface LetterOptions {
  readonly primary: readonly string[];
  readonly more: readonly string[];
}

const splitLetters = (ranked: string, primaryCount: number): LetterOptions => {
  const letters = Array.from(ranked);
  return {
    primary: letters.slice(0, primaryCount),
    more: letters.slice(primaryCount),
  };
};

// The compact model only keys prefixes up to four letters, so a longer word
// is ranked by its longest tail that the model knows.
const rankedNextLetters = (model: KeyboardModel, word: string): string => {
  if (word === "") return model.i;
  const tails = Array.from({ length: word.length }, (_, start) =>
    word.slice(start),
  );
  const known = tails.find((tail) => model.n[tail] !== undefined);
  return known === undefined ? "" : (model.n[known] ?? "");
};

export const letterOptions = (
  model: KeyboardModel,
  word: string,
  primaryCount: number,
): LetterOptions => splitLetters(rankedNextLetters(model, word), primaryCount);

let pendingModel: Promise<KeyboardModel> | undefined;

export const loadKeyboardModel = (): Promise<KeyboardModel> => {
  pendingModel ??= import("../data/keyboard-model.json")
    .then((module) => keyboardModelSchema.parse(module.default))
    .catch((error: unknown) => {
      pendingModel = undefined;
      throw error;
    });
  return pendingModel;
};
